import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Button } from "react-bootstrap";
import React from "react";
import { useTranslation } from "react-i18next";
import { CompanyAccess, RoleProxy } from "@me-team/host/main/graphql/types";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRoleManagementContext } from "../context/RoleManagementContext";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { cloneNameToDisplay } from "../../../Me-menu/utils/cloneNameUtils";

type RoleRowProps = {
    role: RoleProxy;
};

const RoleRow: React.FC<RoleRowProps> = (props) => {
    const { t: translate } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const { isOwner, isDefault, isDeleted, id } = props.role;
    const { onDeleteRole, onCloneRole } = useRoleManagementContext();
    const canEditRole = useRoleAccess(CompanyAccess.RoleEdit);
    const canCloneRole = useRoleAccess(CompanyAccess.RoleCreateClone);
    const canDeleteRole = useRoleAccess(CompanyAccess.RoleDelete);
    const canViewRole = useRoleAccess(CompanyAccess.AccessSettingsView);
    const shouldShowViewRoleButton = !isDeleted && (isDefault || (!canEditRole && canViewRole));
    const roleName = cloneNameToDisplay(props.role.name, translate);

    return (
        <tr>
            <td className={`py-4 px-3 ${isDeleted ? "text-secondary" : ""}`}>{roleName}</td>
            <td className={`py-4 px-3 ${isDeleted ? "text-secondary" : ""}`}>
                {props.role.description ? props.role.description : "-"}
            </td>
            <td className="py-4 px-3">
                <div className="d-flex justify-content-end gap-2">
                    {canEditRole && !isOwner && !isDefault && !isDeleted && (
                        <>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip className="tooltip-category">{translate("Edit role")}</Tooltip>}
                            >
                                <Link
                                    to={`/branch/role-management/${branchId}/roles/${id}/edit`}
                                    className="btn btn-outline-secondary rounded text-dark border d-inline-flex align-items-center justify-content-center"
                                >
                                    <i className="bi bi-pencil d-flex fs-normal"></i>
                                </Link>
                            </OverlayTrigger>
                        </>
                    )}
                    {canCloneRole && !isOwner && !isDeleted && (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip className="tooltip-category">{translate("Clone role")}</Tooltip>}
                        >
                            <Button
                                variant={"outline-secondary"}
                                className={"btn rounded text-dark border"}
                                onClick={() => {
                                    if (!canCloneRole) return;
                                    onCloneRole({ roleId: props.role.id, roleName: props.role.name });
                                }}
                            >
                                <i className="bi bi-stickies d-flex fs-normal"></i>
                            </Button>
                        </OverlayTrigger>
                    )}
                    {shouldShowViewRoleButton && (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip className="tooltip-category">{translate("View role")}</Tooltip>}
                        >
                            <Link
                                to={`/branch/role-management/${branchId}/roles/${id}/view`}
                                className="btn btn-outline-secondary rounded text-dark border d-inline-flex align-items-center justify-content-center"
                            >
                                <i className="bi bi-eye d-flex fs-normal"></i>
                            </Link>
                        </OverlayTrigger>
                    )}
                    {canDeleteRole && !props.role.isDefault && !isDeleted && (
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip className="tooltip-category">{translate("Delete role")}</Tooltip>}
                        >
                            <Button
                                variant={"outline-secondary"}
                                className={"btn rounded text-dark border"}
                                onClick={() => {
                                    if (!canDeleteRole) return;
                                    onDeleteRole({ roleId: props.role.id, roleName });
                                }}
                            >
                                <i className="bi bi-trash3 d-flex fs-normal"></i>
                            </Button>
                        </OverlayTrigger>
                    )}
                    {isDeleted && (
                        <>
                            <OverlayTrigger
                                placement="bottom"
                                overlay={
                                    <Tooltip id="tooltip-top">
                                        {translate("The role is archived, you can restore it at any time")}
                                    </Tooltip>
                                }
                            >
                                <Button variant="secondary" className={"px-3 py-2 me-2 fw-normal rounded-1"}>
                                    {translate("servicesPage.archive")}
                                </Button>
                            </OverlayTrigger>
                            {canDeleteRole && (
                                <OverlayTrigger
                                    placement="bottom"
                                    overlay={
                                        <Tooltip className="tooltip-category">{translate("Restore role")}</Tooltip>
                                    }
                                >
                                    <Button
                                        variant={"outline-secondary"}
                                        className={"btn rounded text-dark border"}
                                        disabled={!canDeleteRole}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (!canDeleteRole) return;
                                            navigate(`/branch/role-management/${branchId}/roles/${id}/restore`);
                                        }}
                                    >
                                        <i className="bi bi-arrow-counterclockwise d-flex fs-normal"></i>
                                    </Button>
                                </OverlayTrigger>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
};

export default React.memo(RoleRow);
